<template>
  <v-container fluid>
    <v-row>
      <v-col cols="2">
        <v-select
          v-model="inventoryClassFilterIds"
          clearable
          hide-details="auto"
          multiple
          label="Class"
          :items="inventoryClasses"
          item-text="name"
          item-value="id"
          :menu-props="{ bottom: true, offsetY: true }"
        >
          <template #item="{ item }">
            <v-chip
              class="mt-1 mb-1"
              small
              text-color="white"
              style="cursor: pointer;"
              :color="item ? item.color : 'white'"
            >
              {{ item ? item.name : '' }}
            </v-chip>
          </template>
          <!-- eslint-disable-next-line vue/no-template-shadow -->
          <template #selection="{ item }">
            <v-chip
              class="mt-1 mb-1"
              small
              text-color="white"
              style="cursor: pointer;"
              :color="item ? item.color : 'white'"
            >
              {{ item ? item.name : '' }}
            </v-chip>
          </template>
        </v-select>
      </v-col>
      <v-col cols="3">
        <v-autocomplete
          v-model="jobFilterId"
          label="Jobs"
          :items="jobs"
          hide-details="auto"
          item-value="id"
          :item-text="item => `${item.code} - ${item.name}; ${item.reference}`"
          clearable
        >
          <template #selection="{ item }">
            <span>
              <span style="font-weight: bold;">{{ item.code ? `${item.code}` : '' }}</span> - {{ item.name ? item.name : '' }} <span style="color: #1976D2;">{{ item.reference ? `; ${item.reference}` : '' }}</span>
            </span>
          </template>

          <template #item="{ item }">
            <span>
              <span style="font-weight: bold;">{{ item.code ? `${item.code}` : '' }}</span> - {{ item.name ? item.name : '' }}<span style="color: #1976D2;">{{ item.reference ? `; ${item.reference}` : '' }}</span>
            </span>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="2" class="pb-0">
        <v-autocomplete
          v-model="statusFilterId"
          label="Status"
          :items="statuses"
          item-text="name"
          item-value="id"
          hide-details="true"
          clearable
        >
          <template #item="{ item }">
            <v-chip
              class="mt-1 mb-1"
              small
              text-color="white"
              style="cursor: pointer;"
              :color="item ? item.color : 'white'"
            >
              {{ item ? item.name : '' }}
            </v-chip>
          </template>
          <!-- eslint-disable-next-line vue/no-template-shadow -->
          <template #selection="{ item }">
            <v-chip
              class="mt-1 mb-1"
              small
              text-color="white"
              style="cursor: pointer;"
              :color="item ? item.color : 'white'"
            >
              {{ item ? item.name : '' }}
            </v-chip>
          </template>
        </v-autocomplete>
      </v-col>
      <v-spacer />
      <v-col class="text-right mt-3" cols="4">
        <v-btn color="primary" outlined @click="showRequestForm()">
          <v-icon small class="mr-1"> fas fa-plus </v-icon>
          New Request
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col class="pt-0 pb-0">
        <v-card elevation="0" outlined>
          <v-data-table 
            :headers="tableHeaders"
            :items="requests"
            :items-per-page="pagination.itemsPerPage"
            :footer-props="footerProps"
            :server-items-length="numberOfRequests"
            :loading="tableIsLoading"
            loading-text="Loading records"
            :no-data-text="isInitialized ? 'No records found' : 'Loading records'"
            @update:options="updateOptions"
            dense >
            <template #item="{ item }">
              <tr>
                <td>
                  <text-highlight :queries="search">
                    {{ item.id }}
                  </text-highlight>
                </td>
                <td class="text-center">
                  <v-chip
                    small
                    :color="item && item.class ? item.class.color : 'black'"
                    text-color="white"
                  >
                    {{ item && item.class ? item.class.name : '' }}
                  </v-chip>
                </td>
                <td>
                  <span v-if="item.job">  
                    <span style="font-weight: bold;">
                      <text-highlight :queries="search">{{ item.job.code ? `${item.job.code}` : '' }}</text-highlight>
                    </span>
                    - 
                    <text-highlight :queries="search">{{ item.job.project ? item.job.project.name : '' }}</text-highlight>
                    <span style="color: #1976D2;">
                      <text-highlight :queries="search">{{ item.job.reference ? `; ${item.job.reference}` : '' }}</text-highlight>
                    </span>
                  </span>
                </td>
                <td>
                  {{ getRowQuantity(item) }}
                </td>
                <td>
                  <text-highlight :queries="search">
                    {{ item.user.first_name }} {{ item.user.last_name }}
                  </text-highlight>
                </td>
                <td>
                  {{ item.created_at }}
                </td>
                <td>
                  <v-chip
                    v-if="statusMap[item.status_id]"
                    small
                    style="width: 100%; max-width: 140px; white-space: nowrap; "
                    :color="statusMap[item.status_id].color"
                    text-color="white"
                  > 
                  {{ statusMap[item.status_id].name }}
                  </v-chip>
                </td>
                <td>
                  {{ item.production_date || '-' }}
                </td>
                <td class="text-end">
                  <v-btn color="primary" small outlined @click="showRequestForm(item.id)">Open</v-btn>

                  <v-menu offset-y>
                    <template #activator="{ on, attrs }">
                      <v-btn class="ml-1 my-1" elevation="0" fab outlined x-small v-bind="attrs" tile color="primary" style="border-radius: 7px; width: 28px !important; height: 28px !important;" v-on="on">
                        <v-icon>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list class="pa-3">
                      <v-list-item :disabled="!isAuthorized(item)" @click="deleteRequest(item.id)">
                        <v-list-item-title :style="`color: ${!isAuthorized(item) ? 'rgba(0,0,0,0.26)' : 'red'}; font-weight: 600;`">
                          <v-row align="center">
                            <v-col cols="auto" class="pr-0" style="white-space: nowrap;">
                              <v-icon style="font-size: 16px;" :color="!isAuthorized(item) ? 'rgba(0, 0, 0, 0.26)' : 'red'">fas fa-trash</v-icon>
                            </v-col>
                            <v-col class="pt-4">DELETE</v-col>
                          </v-row>
                        </v-list-item-title>
                      </v-list-item>

                      <v-list-item :disabled="!isAuthorized(item) || item.schedule_id !== null" @click="issueRequest(item)">
                        <v-list-item-title :style="`color: ${!isAuthorized(item) || item.schedule_id !== null ? 'rgba(0,0,0,0.26)' : 'gray'}; font-weight: 600;`">
                          <v-row align="center">
                            <v-col cols="auto" class="pr-0" style="white-space: nowrap;">
                              <v-icon style="font-size: 16px;" :color="!isAuthorized(item) || item.schedule_id !== null ? 'rgba(0, 0, 0, 0.26)' : 'gray'">fas fa-check</v-icon>
                            </v-col>
                            <v-col class="pt-4">ISSUE REQUEST</v-col>
                          </v-row>
                        </v-list-item-title>
                      </v-list-item>

                      <v-list-item :disabled="!isAuthorized(item) || item.schedule_id === null" @click="undoIssueRequest(item)">
                        <v-list-item-title :style="`color: ${!isAuthorized(item) || item.schedule_id === null ? 'rgba(0,0,0,0.26)' : 'gray'}; font-weight: 600;`">
                          <v-row align="center">
                            <v-col cols="auto" class="pr-0" style="white-space: nowrap;">
                              <v-icon style="font-size: 16px;" :color="!isAuthorized(item) || item.schedule_id === null ? 'rgba(0, 0, 0, 0.26)' : 'gray'">fas fa-check</v-icon>
                            </v-col>
                            <v-col class="pt-4">UNDO ISSUE</v-col>
                          </v-row>
                        </v-list-item-title>
                      </v-list-item>

                    </v-list>
                  </v-menu>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-row justify="center">
      <v-dialog v-model="requestFormIsShown" fullscreen scrollable persistent>
        <ad-hoc-request-form
          ref="requestForm"
          @close-form="closeRequestForm"/>
      </v-dialog>
    </v-row>

    <confirm-form ref="confirmForm" />
  </v-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import AdHocRequestForm from '../../components/forms/panels/AdHocRequestForm.vue';
import ConfirmForm from '../../components/elements/ConfirmForm.vue';
import * as adHocApi from '../../api/WipPanelAdHocRequestApi';

export default {
  components: {
    AdHocRequestForm,
    ConfirmForm
  },
  data: () => ({
    isInitialized: false,
    requestFormIsShown: false,
    requests: [],
    inventoryClasses: [],
    jobs: [],
    statuses: [],
    inventoryClassFilterIds: [],
    jobFilterId: null,
    statusFilterId: null,
    statusMap: {},
    tableIsLoading: false,
    tableHeaders: [
      { text: 'Request #', value: 'id' },
      { text: 'Class', value: 'inventory_class_id', align: 'center' },
      { text: 'Job', value: 'job_id' },
      { text: 'Total Qty', value: 'quantity', sortable: false, },
      { text: 'Requested By', value: 'requested_by_id' },
      { text: 'Created At', value: 'created_at' },
      { text: 'Status', value: 'status_id' },
      { text: 'Panel Required Date', value: 'production_date' },
      { text: 'Actions', sortable: false, class: 'text-end' },
    ],

    numberOfRequests: 0,
    footerProps: {
      itemsPerPageOptions: [25, 100, 500, 1000, -1],
    },
    pagination: {
      page: 1,
      itemsPerPage: 25
    },
    sort: {
      sortBy: 'id',
      sortDirection: 'desc'
    }
  }),
  watch: {
    pagination() {
      if (this.isInitialized) this.loadTableData();
    }, 
    sort() {
      if (this.isInitialized) this.loadTableData();
    },
    search() {
      this.loadTableData();
    },
    inventoryClassFilterIds() {
      this.getJobs();
      this.loadTableData();
    },
    jobFilterId() {
      this.loadTableData();
    },
    statusFilterId() {
      this.loadTableData();
    }
  },
  computed: {
    ...mapGetters([
      'businessRulesGetBoolean',
    ]),
    ...mapState({
      search: (state) => state.search,
      user: (state) => state.user,
    }),
    isAdmin() {
      return this.user.roles.some(x => x.id === 3 || x.id === 38); //Roles: admin or panel admin
    },
  },
  created() {
    Promise.all([
      this.$api.glassPoStatus.get(),
      this.$api.inventoryClass.store({}, 'read'),
      this.getJobs(),
    ]).then(([statuses, inventoryClasses]) => {
      console.log('API requests loaded')
      this.statuses = statuses;
      this.statusMap = statuses.reduce((acc, x) => {
        acc[x.id] = x;
        return acc;
      }, {});
      this.inventoryClasses = inventoryClasses.filter(x => x.id !== 0); //TODO this is a dirty fix to filter all the default "ALL". This will need to be properly updated in the controller
      console.log('API data saved')
    })
      .then(() => this.loadTableData())
      .then(() => this.isInitialized = true)

  },
  methods: {
    // async loadGlassStatuses() {
    //   const statuses = await this.$api.glassPoStatus.get();
    //   this.statusMap = statuses.reduce((acc, x) => {
    //     acc[x.id] = x;
    //     return acc;
    //   }, {});
    // },
    async getJobs() {
      const submitModel = { filters: { inventory_class_ids: this.inventoryClassFilterIds, is_manual: 0 } };
      const res = await this.$api.job.readDDL(submitModel);
      this.jobs = res || [];
    },
    isAuthorized(item) {
      return this.isAdmin || this.user.id === item.requested_by_id;
    }, 
    showRequestForm(requestId = null) {
      this.requestFormIsShown = true;
      setTimeout(() => this.$refs.requestForm.open(requestId), 250);
    },
    closeRequestForm() {
      this.openRequest = null;
      this.requestFormIsShown = false;
      this.loadTableData();
    },
    async loadTableData() {
      this.tableIsLoading = true;
      try {
        const data = {
          filters: {
            inventory_class_ids: this.inventoryClassFilterIds,
            job_id: this.jobFilterId,
            status_id: this.statusFilterId
          },
          pagination: this.pagination,
          sort: this.sort,
          search: this.search
        };
        const res = await adHocApi.readList(data);
        this.requests = res.data;
        this.numberOfRequests = res.total;
      } catch (e) {
        console.error(e);
        this.$bus.$emit('showError', 'There was an error loading table data');
      } finally {
        this.tableIsLoading = false;
      }
    },
    getRowQuantity(row) {
      return row.request_items.reduce((acc, x) => acc += x.quantity, 0);
    },

    updateOptions(newOptions) {
      this.pagination = newOptions;
      this.sort = {
        sortBy: newOptions.sortBy[0],
        sortDirection: newOptions.sortDesc[0],
      };
    },

    async deleteRequest(requestId) {
      if (!confirm('Are you sure?')) return;
      this.$emit('loading');
      try {
        const res = await adHocApi.deleteRequest(requestId);
        if (res) { 
          this.$bus.$emit('showSuccess', 'Ad Hoc Request has been deleted successfully');
          this.loadTableData();
        } else {
          throw new Error('API response does not indicate success');
        }
      } catch (e) {
        console.error(e);
        this.$bus.$emit('showError', 'Failed to delete Ad Hoc Request');
      } finally {
        this.$bus.$emit('loading-stop');
      }
    },
    
    async issueRequest(request) {
      const confirm = await this.$refs.confirmForm.open({ title: `Are you sure you want to issue Ad Hoc Request #${request.id}` });
      if (!confirm) return;
      try {
          this.$bus.$emit('loading');
         const res = await adHocApi.issueRequest(request.id);
         this.$bus.$emit('showSuccess', 'The request has been issued successfully');
         this.loadTableData();
      } catch (e) {
        console.error(e);
        this.$bus.$emit('showError', 'Failed to issue Ad Hoc Request');
      } finally {
        this.$bus.$emit('loading-stop');
      }
    },

    async undoIssueRequest(request) {
      const confirm = await this.$refs.confirmForm.open({ title: `Are you sure you want to undo issue Ad Hoc Request #${request.id}` });
      if (!confirm) return;
      try {
         const res = await adHocApi.undoIssueRequest(request.id);
         this.$bus.$emit('showSuccess', 'The request has been unissued successfully');
         this.loadTableData();
      } catch (e) {
        console.error(e);
        this.$bus.$emit('showError', 'Failed to unissue Ad Hoc Request');
      }
    },
  }
};
</script>
